// src/api/apiClient.ts
// Unified AWS API client. All resources route through api.intrlock.io with the user JWT.

// ── Resource path registry ────────────────────────────────────────────────────
// Maps a short resource key to its URL path segment on api.intrlock.io.
export const RESOURCES: Record<string, { path: string }> = {
  customers:            { path: '/customers' },
  dealers:              { path: '/dealers' },
  integrators:          { path: '/dealers' },
  dealer_customers:     { path: '/dealer-customers' },
  properties:           { path: '/properties' },
  units:                { path: '/units' },
  tenants:              { path: '/people' },
  people:               { path: '/people' },
  devices:              { path: '/devices' },
  call_history:         { path: '/events/calls' },
  telnyx_calls:         { path: '/events/calls' },
  audit_events:         { path: '/events/audit' },
  audit_log:            { path: '/events/audit' },
  notifications:        { path: '/notifications' },
  intercom_configs:     { path: '/intercom-configs' },
  app_activations:      { path: '/activations' },
  users:                { path: '/users' },
  system_roles:         { path: '/roles' },
  video_calls:          { path: '/events/video' },
  system_events:        { path: '/events/system' },
  access_events:        { path: '/events/access' },
  pdk_systems:          { path: '/acs-systems' },
  access_control_systems: { path: '/acs-systems' },
  // seam_systems removed — Seam integration disabled
  bridge_devices:       { path: '/bridge-devices' },
  bridge_channels:      { path: '/bridge-channels' },
  bridge_events:        { path: '/events/bridge' },
  brivo_systems:        { path: '/acs-systems' },
  tenant_properties:    { path: '/tenant-properties' },
  guest_pins:           { path: '/guest-pins' },
  access_schedules:     { path: '/access-schedules' },
  billing_rates:        { path: '/billing-rates' },
  billing_products:     { path: '/billing-products' },
  service_costs:        { path: '/service-costs' },
  sign_products:        { path: '/shop/products' },
  sign_options:         { path: '/shop/options' },
  sign_pricing:         { path: '/shop/pricing' },
  sign_templates:       { path: '/shop/templates' },
  sign_orders:          { path: '/shop/orders' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const AWS_BASE = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

function getToken(): string {
  return localStorage.getItem('intrlock_token') || '';
}

function awsHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getToken()}`,
  };
}

function resolveResource(resource: string): { path: string } {
  const entry = RESOURCES[resource];
  if (!entry) throw new Error(`apiClient: unknown resource "${resource}"`);
  return entry;
}

export function shouldLogoutOn401(urlPath: string): boolean {
  // Only treat 401s from session/identity endpoints as session expiry.
  // Vendor-proxy 401s (Stripe, Printify, PDK, Brivo, Seam, Telnyx) mean the
  // vendor rejected — not that our session is dead.
  return urlPath.startsWith('/auth/') || urlPath === '/users/me' || urlPath.startsWith('/users/me/');
}

const MAX_503_RETRIES = 2;

export function shouldRetry503(method: string | undefined, attempt: number): boolean {
  const verb = (method || 'GET').toUpperCase();
  if (verb !== 'GET') return false;
  return attempt < MAX_503_RETRIES;
}

async function awsFetch(urlPath: string, options: RequestInit = {}, attempt: number = 0): Promise<any> {
  const res = await fetch(`${AWS_BASE}${urlPath}`, {
    ...options,
    headers: { ...awsHeaders(), ...(options.headers || {}) },
  });
  if (res.status === 503 && shouldRetry503(options.method, attempt)) {
    await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
    return awsFetch(urlPath, options, attempt + 1);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    if (res.status === 401 && shouldLogoutOn401(urlPath)) {
      localStorage.removeItem('intrlock_token');
      localStorage.removeItem('intrlock_user');
      window.location.href = '/login';
    }
    throw new Error(`API error ${res.status}: ${text}`);
  }
  // 204 No Content
  if (res.status === 204) return null;
  return res.json();
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Generic POST to an arbitrary AWS API path. Use when the call doesn't
 * map to a CRUD resource (e.g. /intercom/create-kiosk, /admin/push/send).
 */
export async function apiPost(urlPath: string, data: Record<string, any> = {}): Promise<any> {
  return awsFetch(urlPath, { method: 'POST', body: JSON.stringify(data) });
}

/** Authenticated GET for non-CRUD application routes. */
export async function apiRequestGet(urlPath: string): Promise<any> {
  return awsFetch(urlPath);
}

/**
 * List all records for a resource.
 * params are appended as query string (e.g. { property_id: '5' }).
 * Filtering is server-side.
 */
export async function apiList(
  resource: string,
  params?: Record<string, string | number | boolean>
): Promise<any[]> {
  const { path } = resolveResource(resource);
  const qs = params
    ? '?' + new URLSearchParams(params as Record<string, string>).toString()
    : '';
  const result = await awsFetch(`${path}${qs}`);
  return Array.isArray(result) ? result : result?.items || [];
}

/**
 * Get a single record by ID.
 */
export async function apiGet(resource: string, id: number | string): Promise<any> {
  const { path } = resolveResource(resource);
  return awsFetch(`${path}/${id}`);
}

/**
 * Create a new record.
 */
export async function apiCreate(resource: string, data: Record<string, any>): Promise<any> {
  const { path } = resolveResource(resource);
  return awsFetch(path, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing record by ID.
 */
export async function apiUpdate(
  resource: string,
  id: number | string,
  data: Record<string, any>
): Promise<any> {
  const { path } = resolveResource(resource);
  return awsFetch(`${path}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Patch a record (partial update).
 */
export async function apiPatch(
  resource: string,
  id: number | string,
  data: Record<string, any>
): Promise<any> {
  const { path } = resolveResource(resource);
  return awsFetch(`${path}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a record by ID.
 */
export async function apiDelete(resource: string, id: number | string): Promise<any> {
  const { path } = resolveResource(resource);
  return awsFetch(`${path}/${id}`, { method: 'DELETE' });
}
